import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConsolidatedPhoneReportComponent } from '../consolidated-phone-report/consolidated-phone-report.component';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from 'src/app/http.service';

export const WALLET_OPTIONS = [
  { key: 'manual', label: 'Manual', permission: 'manual-topup-withdraw' },
  { key: 'transfer', label: 'Transfer', permission: 'auto-topup-withdraw' },
  { key: 'seamless', label: 'Seamless', permission: 'wallet-topup-withdraw' },
];

@Component({
  selector: 'app-consolidated-wallet',
  templateUrl: './consolidated-wallet.component.html',
  styleUrls: ['./consolidated-wallet.component.scss']
})
export class ConsolidatedWalletComponent implements OnInit, OnDestroy {
  @ViewChild('operation') operation: any;
  companies: any[] = [];
  groups: any[] = [];
  permissions: any = {};
  companyUuid = '';
  reportCompanyUuid = '';
  reportCompanyName = '';
  companyFilter = '';
  field = 'all';
  search = '';
  loading = false;
  ready = false;
  searched = false;
  error = '';
  mode = '';
  player: any = null;
  profile: any = null;
  profileSummary: any = null;
  gameBalances: any[] | null = null;
  profileLoading = false;
  options = WALLET_OPTIONS;
  activityGames: any[] = [];
  activityGame = '';
  activityFrom = '';
  activityTo = '';
  gamesLoading = false;
  private gameRequest = 0;
  private request = 0;
  private destroyed = false;

  constructor(public http: HttpService, private route: ActivatedRoute, private router: Router,
    private cdr: ChangeDetectorRef, private modal: NgbModal) {}

  openPlayerReport(player: any) {
    if (!player) return;
    const modal = this.modal.open(ConsolidatedPhoneReportComponent, {size: 'xl'});
    const report = modal.componentInstance;
    report.playerReport = true;
    report.reportCompanyUuid = this.reportCompanyUuid;
    report.playerLabel = [player.code, player.name].filter(Boolean).join(' / ');
    const mobile = String(player.mobile || '').trim();
    report.matchValue = mobile.includes('*') ? '' : mobile;
    report.closeReport = () => modal.dismiss();
  }

  async ngOnInit() {
    this.companyUuid = this.http.getStaticSelectedCompany();
    this.permissions = await this.http.post('/admin/user/permission/hidden', { company_uuid: this.companyUuid }) || {};
    if (this.destroyed) return;
    const query = this.route.snapshot.queryParams;
    this.reportCompanyUuid = query.report_company || this.companyUuid;
    try {
      const scope:any = await this.http.post('/admin/consolidated-wallet/companies', {report_company_uuid:this.reportCompanyUuid});
      if (!scope?.status || !Array.isArray(scope.companies)) throw Error(scope?.message || 'Unable to load report companies.');
      this.companies = scope.companies;
      this.reportCompanyName = scope.parent?.name || '';
      if (query.player && !this.companies.some(c=>c.uuid===this.companyUuid)) throw Error('Selected company is outside the consolidated report group.');
    } catch (e) { this.error = e.message || 'Unable to load report companies.'; return; }
    if (query.player) {
      this.player = await this.http.post('/admin/player/get/v2', { company_uuid: this.companyUuid, player_uuid: query.player });
      if (this.destroyed) return;
      if (!this.player?.uuid) this.error = 'Player is unavailable in this company. Search again.';
      else this.mode = ['records', 'profile'].includes(query.wallet) && this.allowed(this.permissions).length ? query.wallet : this.allowed(this.permissions).find(o => o.key === query.wallet)?.key
        || this.allowed(this.permissions)[0]?.key || '';
      if (this.mode === 'profile') await this.loadProfile();
    }
    this.ready = true;
  }

  ngOnDestroy() { this.destroyed = true; ++this.request; }
  allowed(permissions: any) { return permissions?.['consolidated-wallet'] == 1 ? this.options.filter(o => permissions?.[o.permission] == 1) : []; }
  get selected() {
    // Always use the live operation selection, including an operator's New Search.
    return this.operation ? (this.operation.player?.uuid ? this.operation.player : null) : this.player;
  }
  get selectedCompany() { return this.operation?.operation_company_uuid || this.companyUuid; }
  get companyName() { return this.companies.find(c => c.uuid === this.selectedCompany)?.name || ''; }
  get busy() { return !!this.operation?.is_loading_function; }
  get visibleGroups() { return this.groups.filter(group => group.error || group.response?.collection?.length); }

  clearResults() { ++this.request; this.groups = []; this.searched = false; this.loading = false; this.error = ''; }

  async companyFilterChanged() {
    this.clearResults();
    this.activityGames = [];
    this.activityGame = '';
    const request = ++this.gameRequest;
    if (!this.companyFilter) { this.gamesLoading = false; return; }
    this.gamesLoading = true;
    const response: any = await this.http.post('/admin/consolidated-wallet/games', { company_uuid: this.companyFilter, report_company_uuid: this.reportCompanyUuid });
    if (request !== this.gameRequest || this.destroyed) return;
    this.gamesLoading = false;
    if (response?.status && Array.isArray(response.games)) this.activityGames = response.games;
    else this.error = response?.message || 'Unable to load games for this company.';
  }

  setActivityPeriod(period: string) {
    const today = new Date(Date.now() + 8 * 60 * 60 * 1000);
    this.activityTo = today.toISOString().slice(0, 10);
    if (period === 'week') today.setUTCDate(today.getUTCDate() - 6);
    if (period === 'month') today.setUTCDate(1);
    this.activityFrom = today.toISOString().slice(0, 10);
    this.clearResults();
  }

  activityChanged() {
    this.clearResults();
    if (this.activityGame && !this.activityFrom) this.setActivityPeriod('day');
  }

  async findPlayers() {
    const term = this.search.trim();
    this.clearResults();
    if (term.length < 3 && !this.activityGame) { this.error = 'Enter at least 3 characters or select a company and game.'; return; }
    if (this.activityGame && (!this.companyFilter || !this.activityFrom || !this.activityTo || this.activityFrom > this.activityTo)) {
      this.error = 'Select a company and valid activity dates.'; return;
    }
    const activity = this.activityGame ? { game: this.activityGame, date_from: this.activityFrom, date_to: this.activityTo, field: this.field, search: term } : null;
    const request = this.request;
    const field = this.field;
    this.loading = true;
    const companies = this.companies.filter(c => !this.companyFilter || c.uuid === this.companyFilter);
    // Bound simultaneous work to three companies; results retain separate pagination.
    for (let i = 0; i < companies.length; i += 3) {
      const results = await Promise.all(companies.slice(i, i + 3).map(async company => {
        try {
          const permissions: any = await this.http.post('/admin/user/permission/hidden', { company_uuid: company.uuid });
          if (!permissions || typeof permissions !== 'object') throw new Error();
          const wallets = this.allowed(permissions);
          if (!wallets.length) return null;
          const response: any = await this.http.post('/admin/consolidated-wallet/search', {
            company_uuid: company.uuid, report_company_uuid: this.reportCompanyUuid, field, search: term,
            status: 1, page_nat: true, current: 1, per_page: 20, ...(activity || {})
          });
          if (!response || !Array.isArray(response.collection)) throw new Error();
          return { company, wallets, response, field, term, activity, loading: false };
        } catch (_) { return { company, error: 'Could not load this company. Retry search.' }; }
      }));
      if (request !== this.request || this.destroyed) return;
      this.groups.push(...results.filter(Boolean));
    }
    this.loading = false;
    this.searched = true;
  }

  async page(group: any, current: number) {
    if (!current || group.loading) return;
    group.loading = true;
    const response: any = await this.http.post('/admin/consolidated-wallet/search', {
      company_uuid: group.company.uuid, report_company_uuid: this.reportCompanyUuid, field: group.field, search: group.term, status: 1,
      page_nat: true, current, per_page: 20, ...(group.activity || {})
    });
    group.loading = false;
    if (response && Array.isArray(response.collection)) group.response = response;
    else group.error = 'Could not load this page. Retry search.';
  }

  async open(player: any, companyUuid: string, wallet: string) {
    if (this.busy) return;
    if (player?.is_suspended == 1 && !['profile', 'records'].includes(wallet)) return;
    if (companyUuid !== this.companyUuid) {
      // Recreate the complete company context (including modal services and banks).
      const url = this.router.createUrlTree(['/lo', companyUuid, 'consolidated-wallet'], {
        queryParams: { player: player.uuid, wallet, report_company: this.reportCompanyUuid }
      });
      window.location.assign(this.router.serializeUrl(url));
      return;
    }
    this.mode = '';
    this.cdr.detectChanges();
    this.player = player;
    await this.router.navigate([], { relativeTo: this.route, queryParams: { player: player.uuid, wallet, report_company: this.reportCompanyUuid } });
    if (!this.destroyed) this.mode = wallet;
    if (!this.destroyed && wallet === 'profile') await this.loadProfile();
  }

  async loadProfile() {
    this.profileLoading = true;
    this.profile = null;
    this.profileSummary = null;
    this.gameBalances = null;
    const uuid = this.player?.uuid;
    const [response, summary, balances]: any[] = await Promise.all([
      this.http.post('/admin/player/get', { company_uuid: this.companyUuid, uuid }),
      this.http.post('/admin/player/summary/get', { company_uuid: this.companyUuid, player_uuid: uuid }),
      this.http.post('/admin/player/game-balances/get', { company_uuid: this.companyUuid, player_uuid: uuid }),
    ]);
    if (this.destroyed || this.player?.uuid !== uuid || this.mode !== 'profile') return;
    this.profileLoading = false;
    if (response?.uuid === uuid) {
      this.profile = response;
      this.gameBalances = balances?.status === true && Array.isArray(balances.accounts) ? balances.accounts : null;
      this.profileSummary = summary && typeof summary === 'object' && summary.status !== false ? summary : null;
    }
    else this.error = 'Unable to load this player profile. Please retry.';
  }

  async showProfile() {
    if (this.selected && !this.busy) await this.open(this.selected, this.selectedCompany, 'profile');
  }

  async switchWallet(wallet: string) {
    const player = this.selected;
    const company = this.selectedCompany;
    if (!player || this.busy || !this.allowed(this.permissions).some(o => o.key === wallet)) return;
    await this.open(player, company, wallet);
  }

  get historyTabs() {
    const tabs = ['consolidated', 'bank'];
    if (this.permissions['manual-topup-withdraw'] || this.permissions['auto-topup-withdraw']) tabs.push('game');
    if (this.permissions['wallet-topup-withdraw']) tabs.push('wallet');
    return tabs;
  }

  async showRecords() {
    if (!this.selected || this.busy) return;
    await this.open(this.selected, this.selectedCompany, 'records');
  }

  async newSearch() {
    if (this.busy) return;
    this.mode = '';
    this.player = null;
    this.cdr.detectChanges();
    await this.router.navigate([], { relativeTo: this.route, queryParams: { report_company: this.reportCompanyUuid } });
  }
}
